import { reactive } from 'vue'
import { getPageData } from '@/api/api.js'
import { nextTick } from 'vue'
import { setupHeroGreenBar, disposeHeroGreenBar } from '@/utils/heroGreenBar'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * GLOBAL STATE STORE - CRF Fondazione di Risparmio di Fossano Vue App
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Gestisce lo stato globale dell'applicazione Vue con:
 * - Header/Footer globali
 * - Contenuti delle pagine correnti
 * - Dati di News, Informative, Filiali e Attività
 * - Gestione dello stato di caricamento
 * 
 * Struttura URL: /fondazione/{sezione}/{item-slug}
 * ═══════════════════════════════════════════════════════════════════════════
 */

export const state = reactive({
  /* ─────────────────────────────────────────────────────────────────
     HEADER E FOOTER GLOBALI
     ───────────────────────────────────────────────────────────────── */
  header: {
    logoMain: { image: '', link: '/' },
    menuItems: [],
    actionIcons: [],
    logoSecondary: { image: '', link: '/' },
    homeBanner: { image: '', altText: '' },
  },
  footer: { text: '' },

  /* ─────────────────────────────────────────────────────────────────
     CONTENUTI DELLA PAGINA CORRENTE
     ───────────────────────────────────────────────────────────────── */
  page: {},
  pageSections: [],
  pageSubmenu: [],

  /* ─────────────────────────────────────────────────────────────────
     HERO SECTION - Gestione copertine carousel
     ───────────────────────────────────────────────────────────────── */
  coverImages: [],
  currentCover: 0,
  heroMobileVideo: null,

  /* ─────────────────────────────────────────────────────────────────
     ADATTATORI UI - Per landing pages (Attività)
     ───────────────────────────────────────────────────────────────── */
  pageCards: [],
  pageSubpages: {},

  /* ─────────────────────────────────────────────────────────────────
     MEGA MENU - Navigazione globale
     ───────────────────────────────────────────────────────────────── */
  megaMenuItems: [],
  megaMenuTopActions: [],

  /* ─────────────────────────────────────────────────────────────────
     BANNERS - Avvisi e notifiche globali
     ───────────────────────────────────────────────────────────────── */
  banners: [],

  /* ─────────────────────────────────────────────────────────────────
     ATTIVITÀ - Struttura: Macroaree → Categorie → Attività
     ───────────────────────────────────────────────────────────────── */
  macroaree: [],      // Array di macroaree con eyebrow
  categorie: {},      // { [macroId]: { [catSlug]: {title, description, cards, ...} } }
  attivita: {},       // Indice piatto: { 'macroId/catSlug/attivitaSlug': Attività }

  /* ─────────────────────────────────────────────────────────────────
     STATO GLOBALE
     ───────────────────────────────────────────────────────────────── */
  loading: false,
  error: null,
  notFound: false
})

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CARICAMENTO PAGINA - Endpoint singolo "polpettone"
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Carica tutti i contenuti della pagina da un endpoint API unico
 * Gestisce: header, footer, banner, sections, attivita, hero
 * Gestisce anche gli errori 404 dal backend
 * 
 * @param {string} apiUrl - Endpoint API (es: '/home', '/attivita', '/news')
 */
export async function loadPageContent (apiUrl) {
  state.loading = true
  state.error   = null
  state.notFound = false

  try {
    const data = await getPageData(apiUrl)

    // Header e Footer globali
    state.header        = data.global.header || []
    state.footer        = data.global.footer || []
    state.megaMenuItems = data.global?.megaMenu?.columns || []
    state.megaMenuTopActions = data.global?.megaMenu?.topActions || []

    // Banners (avvisi e notifiche)
    state.banners = data.banners?.items || []

    // Pagina corrente
    state.page          = data.page || {}
    state.pageSubmenu   = data.page.submenu || []
    state.pageSections  = data.page?.sections || []

    // Hero section - Gestione copertine carousel
    const hero = Array.isArray(state.pageSections) ? state.pageSections.find(s => s.type === 'section-hero') : null
    state.coverImages     = hero?.content?.coverImages || []
    state.currentCover    = 0
    state.heroMobileVideo = hero?.content?.mobileVideo || null

    // Attività - Nuova struttura macroaree/categorie
    state.macroaree = data.page?.macroaree || []
    state.categorie = data.page?.categorie || {}

    // Indice piatto per lookup veloce
    state.attivita = {}
    for (const [macroId, cats] of Object.entries(state.categorie)) {
      for (const [catSlug, catObj] of Object.entries(cats || {})) {
        const cards = Array.isArray(catObj.cards) ? catObj.cards : []
        for (const card of cards) {
          const key = `${macroId}/${catSlug}/${card.slug}`
          state.attivita[key] = {
            ...card,
            macroId,
            categoriaSlug: catSlug,
            categoriaTitle: catObj.title,
            categoriaDescription: catObj.description,
            colorScheme: catObj.colorScheme,
            image: card.image || null
          }
        }
      }
    }

    // Adattatori per UI esistente
    state.pageCards    = state.macroaree
    state.pageSubpages = state.categorie

    // Classe CSS speciale per homepage
    const isHome = apiUrl === '/home'
    const root = document.documentElement
    root.classList.toggle('home', isHome)

  } catch (err) {
    console.error('Errore durante il caricamento della pagina:', err)
    
    // ✅ Gestione centralizzata degli errori 404 dal backend
    if (err.response && err.response.status === 404) {
      console.warn(`Pagina non trovata (404): ${apiUrl}`)
      state.notFound = true
      return // Esci senza settare ulteriori errori
    }
    
    if (err.response) console.error('Errore risposta server:', err.response.data)
    state.error = err
  } finally {
    state.loading = false
    // Gestione hero green-bar dopo render
    await nextTick()
    if (apiUrl === '/home') {
      disposeHeroGreenBar()
      setupHeroGreenBar()
    } else {
      disposeHeroGreenBar()
    }
  }
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ERROR HANDLING - 404 Centralizzato
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Setta il flag di pagina non trovata
 * Usato dalle pagine di dettaglio quando non trovano il contenuto richiesto
 */
export function setNotFound () {
  state.notFound = true
}

/**
 * Resetta il flag di pagina non trovata
 */
export function resetNotFound () {
  state.notFound = false
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HELPERS PAGINA - Sezioni e Submenu
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Sovrascrive le sezioni pagina (usato per sottopagine dinamiche)
 * @param {Array} sections - Array di sezioni
 */
export function setSections (sections = []) {
  state.pageSections = sections
}

/**
 * Ritorna il submenu della pagina corrente
 * @returns {Array} Array di voci submenu
 */
export function getPageSubmenu () {
  return Array.isArray(state.pageSubmenu) ? state.pageSubmenu : []
}

/**
 * Genera voci submenu dalle sezioni con eyebrow
 * Usato per navigazione interna pagina (scroll anchors)
 * @returns {Array} Array di {label, target}
 */
export function getSubmenuSections () {
  if (!Array.isArray(state.pageSections)) return []
  return state.pageSections
    .filter(s => s.content?.eyebrow)
    .map(s => {
      let label = s.content.eyebrow
      if (label === state.page.title) label = label + ' - dettagli'
      return {
        label,
        target: '#' + s.content.eyebrow.toLowerCase().replace(/\s+/g, '-')
      }
    })
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HELPERS HERO - Gestione carousel copertine
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Imposta l'indice della copertina attuale
 * @param {number} index - Indice copertina
 */
export function setHeroCover (index = 0) {
  if (typeof index === 'number') state.currentCover = index
}

/**
 * Resetta l'indice della copertina a 0
 */
export function resetHeroCover () {
  state.currentCover = 0
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HELPERS NEWS - Gestione articoli e notizie
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Raccoglie tutte le news da sections news-carousel e news-grid
 * @returns {Array} Array di news
 */
export function getAllNews() {
  const allNews = []
  state.pageSections.forEach(section => {
    if (section.type === 'news-carousel' || section.type === 'news-grid') {
      if (section.content?.items) {
        allNews.push(...section.content.items)
      }
    }
  })
  return allNews
}

/**
 * Trova una news per slug
 * @param {string} slug - Slug della news
 * @returns {Object|undefined} News object
 */
export function getNewsBySlug(slug) {
  const allNews = getAllNews()
  return allNews.find(news => news.slug === slug)
}

/**
 * Trova una news per ID
 * @param {string|number} id - ID della news
 * @returns {Object|undefined} News object
 */
export function getNewsById(id) {
  const allNews = getAllNews()
  return allNews.find(news => news.id === id)
}

/**
 * Genera link completo per news da ID
 * @param {string|number} id - ID della news
 * @returns {string} URL /fondazione/news/{slug} o fallback a /fondazione/news
 */
export function getNewsLinkById(id) {
  const news = getNewsById(id)
  return news ? `/fondazione/news/${news.slug}` : '/fondazione/news'
}

/**
 * Filtra sezioni per ottenere solo news (usato per pagina lista news)
 * @returns {Array} Array di sections tipo news-carousel o news-grid
 */
export function getNewsListSections() {
  return state.pageSections.filter(s =>
    s.type === 'news-carousel' || s.type === 'news-grid'
  )
}

/**
 * Imposta i dati della pagina per una news specifica
 * Carica slug della news e popola state.page e state.pageSections
 * @param {string} slug - Slug della news
 * @returns {boolean} true se trovata, false altrimenti
 */
export function setNewsPageData(slug) {
  const news = getNewsBySlug(slug)
  if (!news) return false

  state.page = {
    ...state.page,
    title: news.title,
    date: news.date,
    image: news.image,
    slug: news.slug,
    id: news.id,
    eyebrow: news.eyebrow
  }

  state.pageSections = news.sections || []
  return true
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HELPERS INFORMATIVE AL PUBBLICO - Gestione informative bancarie
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Raccoglie tutte le informative da sections informative-grid
 * @returns {Array} Array di informative
 */
export function getAllInformative() {
  const allInformative = []
  state.pageSections.forEach(section => {
    if (section.type === 'informative-grid') {
      if (section.content?.items) {
        allInformative.push(...section.content.items)
      }
    }
  })
  return allInformative
}

/**
 * Trova un'informativa per slug
 * @param {string} slug - Slug dell'informativa
 * @returns {Object|undefined} Informativa object
 */
export function getInformativaBySlug(slug) {
  const allInformative = getAllInformative()
  return allInformative.find(info => info.slug === slug)
}

/**
 * Trova un'informativa per ID
 * @param {string|number} id - ID dell'informativa
 * @returns {Object|undefined} Informativa object
 */
export function getInformativaById(id) {
  const allInformative = getAllInformative()
  return allInformative.find(info => info.id === id)
}

/**
 * Genera link completo per informativa da ID
 * @param {string|number} id - ID dell'informativa
 * @returns {string} URL /fondazione/informative-al-pubblico/{slug} o fallback
 */
export function getInformativaLinkById(id) {
  const informativa = getInformativaById(id)
  return informativa ? `/fondazione/informative-al-pubblico/${informativa.slug}` : '/fondazione/informative-al-pubblico'
}

/**
 * Imposta i dati della pagina per un'informativa specifica
 * Carica slug dell'informativa e popola state.page e state.pageSections
 * @param {string} slug - Slug dell'informativa
 * @returns {boolean} true se trovata, false altrimenti
 */
export function setInformativaPageData(slug) {
  const informativa = getInformativaBySlug(slug)
  if (!informativa) return false

  state.page = {
    ...state.page,
    title: informativa.title,
    date: informativa.date,
    eyebrow: informativa.eyebrow,
    slug: informativa.slug,
    id: informativa.id,
    image: informativa.image
  }

  state.pageSections = informativa.sections || []
  return true
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HELPERS ATTIVITÀ - Struttura: Macroaree → Categorie → Attività
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Ritorna tutte le macroaree (landing /attivita)
 * @returns {Array} Array di macroaree con proprietà title, eyebrow, etc.
 */
export function getMacroaree () {
  return Array.isArray(state.macroaree) ? state.macroaree : []
}

/**
 * Trova una macroarea per ID
 * @param {string} macroId - ID della macroarea
 * @returns {Object|undefined} Macroarea object
 */
export function getMacroareaById (macroId) {
  return getMacroaree().find(m => m.id === macroId)
}

/**
 * Ottiene mappa categorie di una macroarea
 * @param {string} macroId - ID della macroarea
 * @returns {Object} { [catSlug]: {title, description, cards, ...} }
 */
export function getCategorieByMacro (macroId) {
  return state.categorie?.[macroId] || {}
}

/**
 * Trova una categoria specifica
 * @param {string} macroId - ID della macroarea
 * @param {string} categoriaSlug - Slug della categoria
 * @returns {Object|undefined} Categoria object
 */
export function getCategoria (macroId, categoriaSlug) {
  return getCategorieByMacro(macroId)?.[categoriaSlug]
}

/**
 * Ottiene lista attività (cards) dentro una categoria
 * @param {string} macroId - ID della macroarea
 * @param {string} categoriaSlug - Slug della categoria
 * @returns {Array} Array di attività/cards
 */
export function getAttivita (macroId, categoriaSlug) {
  const cat = getCategoria(macroId, categoriaSlug)
  return Array.isArray(cat?.cards) ? cat.cards : []
}

/**
 * Trova un'attività specifica
 * @param {string} macroId - ID della macroarea
 * @param {string} categoriaSlug - Slug della categoria
 * @param {string} attivitaSlug - Slug dell'attività
 * @returns {Object|undefined} Attività object
 */
export function getAttivita_single (macroId, categoriaSlug, attivitaSlug) {
  return getAttivita(macroId, categoriaSlug).find(p => p.slug === attivitaSlug)
}

/**
 * Imposta i dati della pagina per una categoria di attività
 * @param {string} macroId - ID della macroarea
 * @param {string} categoriaSlug - Slug della categoria
 * @returns {boolean} true se trovata, false altrimenti
 */
export function setCategoriaPageData (macroId, categoriaSlug) {
  const macro = getMacroareaById(macroId)
  const cat   = getCategoria(macroId, categoriaSlug)
  if (!macro || !cat) return false

  state.page = {
    ...state.page,
    title: cat.title,
    slug: categoriaSlug,
    eyebrow: macro.title,
    description: cat.description,
    colorScheme: cat.colorScheme
  }
  return true
}

/**
 * Imposta i dati della pagina per un'attività specifica
 * @param {string} macroId - ID della macroarea
 * @param {string} categoriaSlug - Slug della categoria
 * @param {string} attivitaSlug - Slug dell'attività
 * @returns {boolean} true se trovata, false altrimenti
 */
export function setAttivitaPageData (macroId, categoriaSlug, attivitaSlug) {
  const attivita = getAttivita_single(macroId, categoriaSlug, attivitaSlug)
  const cat      = getCategoria(macroId, categoriaSlug)
  if (!attivita || !cat) return false

  state.page = {
    ...state.page,
    title: attivita.label,
    slug: attivitaSlug,
    eyebrow: cat.title,
    description: cat.description,
    image: attivita.image
  }
  state.pageSections = Array.isArray(attivita.sections) ? attivita.sections : []
  return true
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HELPERS BANNERS - Gestione avvisi e notifiche
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Ritorna tutti i banners attivi
 * @returns {Array} Array di banners
 */
export function getBanners() {
  return Array.isArray(state.banners) ? state.banners : []
}

/**
 * Verifica se ci sono banners da mostrare
 * @returns {boolean} true se banners presenti
 */
export function hasBanners() {
  return getBanners().length > 0
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HELPERS FILIALI - Gestione punti filiali geografici
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Ottiene tutti i gruppi di filiali (per area geografica)
 * @returns {Array} Array di filiali-group sections
 */
export function getFilialiGroups() {
  const sections = Array.isArray(state.page?.sections)
    ? state.page.sections
    : (Array.isArray(state.pageSections) ? state.pageSections : [])
  return sections.filter(s => s && s.type === 'filiali-group')
}

/**
 * Raccoglie tutte le filiali da tutti i gruppi
 * @returns {Array} Array di filiali con proprietà title, address, city, slug, etc.
 */
export function getAllFiliali() {
  const filiali = []
  const groups = getFilialiGroups()
  groups.forEach(group => {
    const items = group?.content?.items || []
    filiali.push(...items)
  })
  return filiali
}

/**
 * Trova una filiale per slug
 * @param {string} slug - Slug della filiale (es: 'fossano')
 * @returns {Object|undefined} Filiale object
 */
export function getFilialeBySlug(slug) {
  if (!slug) return undefined
  const allFiliali = getAllFiliali()
  return allFiliali.find(f => f.slug === slug)
}

/**
 * Imposta i dati della pagina per una filiale specifica
 * @param {string} slug - Slug della filiale
 * @returns {boolean} true se trovata, false altrimenti
 */
export function setFilialePageData(slug) {
  const filiale = getFilialeBySlug(slug)
  if (!filiale) return false

  state.page = {
    ...state.page,
    title: filiale.title,
    address: filiale.address,
    cap: filiale.cap,
    city: filiale.city,
    phone: filiale.phone,
    fax: filiale.fax,
    email: filiale.email,
    responsabile: filiale.responsabile,
    text: filiale.text,
    image: filiale.image,
    mapsUrl: filiale.mapsUrl,
    addressLabel: filiale.addressLabel,
    phoneLabel: filiale.phoneLabel,
    faxLabel: filiale.faxLabel,
    emailLabel: filiale.emailLabel,
    responsabileLabel: filiale.responsabileLabel,
    orariTitle: filiale.orariTitle,
    orario: filiale.orario,
    hours: filiale.hours,
    slug: filiale.slug
  }

  state.pageSections = filiale.sections || []
  return true
}